import React from 'react';
import { 
  ChevronRight, 
  BarChart3, 
  Calendar, 
  Users, 
  Package, 
  TrendingUp, 
  Shield, 
  Zap,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { Logo } from '@/assets/logo';
import { useLocation } from 'wouter';

function LandingPage() {
      const [location, setLocation] = useLocation();
  const stages = [
    { name: 'Planner', icon: Calendar, description: 'Strategic planning and forecasting' },
    { name: 'Appointment', icon: Users, description: 'Schedule and manage meetings' },
    { name: 'Sales', icon: TrendingUp, description: 'Track sales performance and conversions' },
    { name: 'Courier', icon: Package, description: 'Monitor order completion and delivery' },
    { name: 'Invoicing', icon: Package, description: 'Monitor order completion and delivery' },
    { name: 'Warehouse', icon: Package, description: 'Monitor order completion and delivery' }
  ];

  const features = [
    { 
      icon: BarChart3, 
      title: 'Real-time Analytics', 
      description: 'Get instant insights into your PO performance with advanced dashboard analytics' 
    },
    { 
      icon: Shield, 
      title: 'Secure & Compliant', 
      description: 'Enterprise-grade security with full audit trails and compliance reporting' 
    },
    { 
      icon: Zap, 
      title: 'Automated Workflows', 
      description: 'Streamline your processes with intelligent automation and notifications' 
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center p-4">
              {/* <div className=""> */}
                {/* <div className="w-8 h-8 bg-gradient-to-br from-[#7434d5] to-[#9c4ed4] rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">E</span>
                </div> */}
                {/* <span className="text-xl font-bold text-gray-900"> */}
                    <Logo className='h-16 w-full' />
                {/* </span> */}
              {/* </div> */}
            </div>
            <button 
            onClick={()=>{
                setLocation('/dashboard')
            }}
              className="bg-[#7434d5] hover:bg-[#6329c7] text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 via-white to-purple-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Revolutionize Your{' '}
              <span className="bg-gradient-to-r from-[#7434d5] to-[#9c4ed4] bg-clip-text text-transparent">
                PO Management
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Track purchase orders across multiple stages with our comprehensive dashboard. 
              From planning to fulfillment, get complete visibility and control over your entire PO lifecycle.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={()=>{setLocation('/dashboard')}} className="bg-[#7434d5] hover:bg-[#6329c7] text-white px-6 py-3 rounded-xl font-semibold text-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center group">
                Dashboard
                <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
              {/* <button className="border-2 border-[#7434d5] text-[#7434d5] hover:bg-[#7434d5] hover:text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200">
                Watch Demo
              </button> */}
            </div>
          </div>
        </div>
      </section>

      {/* Stages Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Track Every Stage of Your PO Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From initial planning to final delivery, monitor and optimize every step of your purchase order process
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 place-items-center">
            {stages.map((stage, index) => (
              <div key={stage.name} className="relative group">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-[#7434d5]/20 transform hover:-translate-y-2">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#7434d5] to-[#9c4ed4] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <stage.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{stage.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{stage.description}</p>
                </div>
                {index < stages.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="h-8 w-8 text-[#7434d5]/30" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Powerful Dashboard at Your Fingertips
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get comprehensive insights with our intuitive dashboard designed for modern PO management
            </p>
          </div>
          
          <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-200">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-gradient-to-r from-[#7434d5]/10 to-[#9c4ed4]/10 p-6 rounded-2xl border border-[#7434d5]/20">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">PO Performance Overview</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-[#7434d5]">248</div>
                      <div className="text-sm text-gray-600">Active POs</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-500">94%</div>
                      <div className="text-sm text-gray-600">On Time</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-500">$2.4M</div>
                      <div className="text-sm text-gray-600">Total Value</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-2xl">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Activities</h3>
                  <div className="space-y-3">
                    {['PO-2024-001 moved to Sales stage', 'New appointment scheduled for PO-2024-045', 'PO-2024-023 completed successfully'].map((activity, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span className="text-gray-700">{activity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-[#7434d5] text-white p-6 rounded-2xl">
                  <h3 className="text-lg font-bold mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <button className="w-full bg-white/20 hover:bg-white/30 text-white p-3 rounded-lg transition-colors">
                      Create New PO
                    </button>
                    <button className="w-full bg-white/20 hover:bg-white/30 text-white p-3 rounded-lg transition-colors">
                      Track PO
                    </button>
                    <button className="w-full bg-white/20 hover:bg-white/30 text-white p-3 rounded-lg transition-colors">
                      Invite members
                    </button>
                  </div>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-2xl">
                  <h3 className="text-lg font-bold text-yellow-800 mb-2">Alerts</h3>
                  <p className="text-yellow-700 text-sm">3 POs require your attention</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Enlytical?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Built for modern businesses that demand efficiency, transparency, and growth
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div key={feature.title} className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-[#7434d5] to-[#9c4ed4] rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#7434d5] to-[#9c4ed4]">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your PO Management?
          </h2>
          <p className="text-xl text-purple-100 mb-8 leading-relaxed">
            Join thousands of businesses already using Enlytical to streamline their purchase order processes
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white hover:bg-gray-100 text-[#7434d5] px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 transform hover:scale-105 shadow-lg">
              Start Free Trial
            </button>
            <button className="border-2 border-white text-white hover:bg-white hover:text-[#7434d5] px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200">
              Contact Sales
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              {/* <div className="w-8 h-8 bg-gradient-to-br from-[#7434d5] to-[#9c4ed4] rounded-lg flex items-center justify-center"> */}
                {/* <span className="text-white font-bold text-sm">E</span> */}
              {/* </div/> */}
              <span className="text-xl font-bold">Enlytical</span>
            </div>
            <p className="text-gray-400">© 2025 Enlytical. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;